import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { DEPT_LABELS } from '../constants';
import type { CreateUserPayload } from '../types';

interface CreateUserModalProps {
  open: boolean;
  /** Default department (the admin's own dept). Superadmin may override. */
  department: string;
  /** Superadmin can choose role + department; admin is locked to employee + own dept. */
  isSuperAdmin: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateUserPayload) => void;
}

type Role = 'admin' | 'employee';
type FormState = {
  username: string;
  displayName: string;
  password: string;
  shiftType: 'domestic' | 'international';
  role: Role;
  department: string;
};

/** Departments the backend accepts on user creation (note: 'management' is not creatable). */
const CREATE_DEPTS = ['sales', 'hr', 'it', 'digital_marketing'];

export function CreateUserModal({ open, department, isSuperAdmin, loading, onClose, onSubmit }: CreateUserModalProps) {
  const initialDept = CREATE_DEPTS.includes(department) ? department : 'sales';
  const empty: FormState = {
    username: '',
    displayName: '',
    password: '',
    shiftType: 'domestic',
    role: 'employee',
    department: initialDept,
  };
  const [form, setForm] = useState<FormState>(empty);

  useEffect(() => {
    if (open) setForm(empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, department]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username.trim() || !form.displayName.trim() || form.password.length < 6) return;
    onSubmit({
      username: form.username.trim().toLowerCase(),
      displayName: form.displayName.trim(),
      password: form.password,
      // Superadmin chooses dept; admin is scoped to their own department.
      department: isSuperAdmin ? form.department : department,
      shiftType: form.shiftType,
      // Backend forces 'employee' for non-superadmin regardless.
      role: isSuperAdmin ? form.role : 'employee',
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create User"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="create-user-form" loading={loading}>
            Create User
          </Button>
        </>
      }
    >
      <form id="create-user-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Username *"
          name="new-user-username"
          value={form.username}
          onChange={(e) => set('username', e.target.value)}
          placeholder="firstname"
          hint="2–30 chars, start with a letter; letters/numbers/dots/dashes"
          required
        />
        <Input
          label="Display Name *"
          name="new-user-displayname"
          value={form.displayName}
          onChange={(e) => set('displayName', e.target.value)}
          placeholder="Full name"
          required
        />
        <Input
          label="Temporary Password *"
          name="new-user-password"
          type="password"
          value={form.password}
          onChange={(e) => set('password', e.target.value)}
          placeholder="At least 6 characters"
          required
        />

        {/* Role + Department */}
        {isSuperAdmin ? (
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Role"
              name="new-user-role"
              value={form.role}
              onChange={(e) => set('role', e.target.value as Role)}
              options={[
                { value: 'employee', label: 'Employee' },
                { value: 'admin', label: 'Administrator' },
              ]}
            />
            <Select
              label="Department"
              name="new-user-department"
              value={form.department}
              onChange={(e) => set('department', e.target.value)}
              options={CREATE_DEPTS.map((d) => ({ value: d, label: DEPT_LABELS[d] ?? d }))}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Shift"
              name="new-user-shift"
              value={form.shiftType}
              onChange={(e) => set('shiftType', e.target.value as 'domestic' | 'international')}
              options={[
                { value: 'domestic', label: 'Domestic' },
                { value: 'international', label: 'International' },
              ]}
            />
            <Input label="Department" name="new-user-department-locked" value={DEPT_LABELS[department] ?? department} readOnly disabled />
          </div>
        )}

        {/* Shift for superadmin (separate row so the grid above stays Role+Dept) */}
        {isSuperAdmin && (
          <Select
            label="Shift"
            name="new-user-shift-sa"
            value={form.shiftType}
            onChange={(e) => set('shiftType', e.target.value as 'domestic' | 'international')}
            options={[
              { value: 'domestic', label: 'Domestic' },
              { value: 'international', label: 'International' },
            ]}
          />
        )}

        <p className="text-body-sm text-on-surface-variant">
          {isSuperAdmin
            ? 'As the executive, you can assign the role and department for this user.'
            : 'New users are created as employees in your department.'}
        </p>
      </form>
    </Modal>
  );
}
